import { create } from "zustand";

export const useCardsStore = create((set, get) => ({
  data: [],
  card: undefined,
  hoverCard: undefined,
  action: undefined,
  cardsTab: undefined,
  cardsTabs: undefined,
  isPromptLoading: false,

  setCardsTab: (value) => set(() => ({ cardsTab: value })),
  setCardsTabs: (value) => set(() => ({ cardsTabs: value })),

  setIsPromptLoading: (value) => set(() => ({ isPromptLoading: value })),

  getData: () => get().data,

  setData: (value, clear = false, isUndo = false, isRedo = false) =>
    set((state) => {
      const isArray = Array.isArray(value);

      if (clear) {
        const data = isArray ? [...value] : [value];
        if (!isUndo && !isRedo) {
          get().pushToHistory({
            data,
            cardsTabs: { ...get().cardsTabs },
          });
        }
        return { data };
      }

      const data = isArray ? [...state.data, ...value] : [...state.data, value];

      if (!isUndo && !isRedo) {
        get().pushToHistory({
          data,
          cardsTabs: { ...get().cardsTabs },
        });
      }

      return {
        data,
      };
    }),

  removeCard: (id) => {
    const updatedData = [...get().data].filter((item) => item.card.id !== id);
    get().pushToHistory({
      data: updatedData,
      cardsTabs: { ...get().cardsTabs },
    });
    set((state) => ({
      data: updatedData,
      card: undefined,
    }));
  },
  removeCards: (value) => {
    get().pushToHistory({
      data: value,
      cardsTabs: { ...get().cardsTabs },
    });
    set(() => ({ data: value }));
  },
  moveCard: (id, direction) => {
    set((state) => {
      const index = state.data.findIndex((item) => item.card.id === id);
      if (index === -1) return state;

      let newIndex = direction === "left" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= state.data.length) return state;

      const updatedData = [...state.data];
      const [movedCard] = updatedData.splice(index, 1);
      updatedData.splice(newIndex, 0, movedCard);

      get().pushToHistory({
        data: updatedData,
        cardsTabs: { ...get().cardsTabs },
      });

      return { data: updatedData };
    });
  },

  setCard: (value) =>
    set((state) => {
      if (value) {
        return {
          card: state.card?.card?.id === value.card.id ? undefined : value,
        };
      } else return { card: undefined };
    }),
  setAction: (value) => set(() => ({ action: value })),
  setHoverCard: (value) => set(() => ({ hoverCard: value })),
  updateCardConfig: ({ id, key, value }) => {
    const updatedData = [...get().data].map((data) =>
      data.card.id === id
        ? {
            ...data,
            config: { ...data.config, [key]: value },
          }
        : data
    );

    get().pushToHistory({
      data: updatedData,
      cardsTabs: { ...get().cardsTabs },
    });

    set((state) => ({
      data: updatedData,
      card: state.action?.card
        ? {
            ...state.action.card,
            config: { ...state.action.card.config, [key]: value },
          }
        : { ...state.card, config: { ...state.card.config, [key]: value } },
    }));
  },
  updateCardData: ({ id, key, value }) => {
    const updatedData = [...get().data].map((data) =>
      data.card.id === id
        ? {
            ...data,
            card: { ...data.card, [key]: value },
          }
        : data
    );

    get().pushToHistory({
      data: updatedData,
      cardsTabs: { ...get().cardsTabs },
    });

    set((state) => ({
      data: updatedData,
      card: !!state.action?.card
        ? {
            ...state.action.card,
            card: { ...(state.action.card?.card || {}), [key]: value },
          }
        : { ...state?.card, card: { ...state.card.card, [key]: value } },
    }));
  },
  updateCardsConfig: ({ key, value }) => {
    const updatedData = [...get().data].map((data) => ({
      ...data,
      config: { ...data.config, [key]: value },
    }));

    get().pushToHistory({
      data: updatedData,
      cardsTabs: { ...get().cardsTabs },
    });

    set((state) => ({
      data: updatedData,
      card:
        typeof state.card === "object"
          ? { ...state.card, config: { ...state.card.config, [key]: value } }
          : undefined,
    }));
  },
  updateCardsData: ({ key, value }) => {
    const updatedData = [...get().data].map((data) => ({
      ...data,
      card: { ...data.card, [key]: value },
    }));

    get().pushToHistory({
      data: updatedData,
      cardsTabs: { ...get().cardsTabs },
    });

    set((state) => ({
      data: updatedData,
      card:
        typeof state.card === "object"
          ? { ...state.card, card: { ...state.card.card, [key]: value } }
          : undefined,
    }));
  },
  updateCardsTabs: (value) => {
    const updatedData = get().data.map((data) => ({ ...data, tabs: value }));
    get().pushToHistory({
      data: updatedData,
      cardsTabs: { ...get().cardsTabs },
    });
    set(() => ({ data: updatedData }));
  },

  // history: { data, tabs }
  // undo redo
  history: [],
  pushToHistory: (value) => {
    const newHistory = [...get().history, value];

    set(() => ({
      history: newHistory,
      position: newHistory.length - 1,
    }));
  },

  position: 0,
  updatePosition: (value) => {
    set(() => ({ position: value }));
  },

  undo: () => {
    const { history, position, setData, setCardsTabs, card } = get();
    const { data, cardsTabs } = history[position - 1];
    const currentlySelectedCard = data?.find(
      (item) => item.card.id === card?.card?.id
    );
    if (currentlySelectedCard) {
      set({ card: currentlySelectedCard });
    }
    setData(data, true, true, false);
    setCardsTabs(cardsTabs);
    set({ position: position - 1 });
  },
  redo: () => {
    const { history, position, setData, setCardsTabs, card } = get();
    const { data, cardsTabs } = history[position + 1];
    const currentlySelectedCard = data?.find(
      (item) => item.card.id === card?.card?.id
    );
    if (currentlySelectedCard) {
      set({ card: currentlySelectedCard });
    }
    setData(data, true, false, true);
    setCardsTabs(cardsTabs);
    set({ position: position + 1 });
  },

  canUndo: () => {
    const { position } = get();
    return position > 0;
  },
  canRedo: () => {
    const { history, position } = get();
    return position < history.length - 1;
  },

  resetStore: () => {
    set({
      data: [],
      card: undefined,
      hoverCard: undefined,
      action: undefined,
      cardsTab: undefined,
      cardsTabs: undefined,
      isPromptLoading: false,
      position: 0,
      history: [],
    });
  },
}));
